<template>
    <div class="mobile-header-container ppv2-bg-white dark:ppv2-bg-dark ppv2-shadow-sm" :style="{ zIndex: sidebarOpen ? 1 : 50 }">
        <!-- Onboarding Banner -->
        <OnboardingBanner
            v-if="showOnboarding && !isAllOnboardingCompleted"
            :current-step="currentOnboardingStep"
            :total-steps="onboardingSteps.length"
            :current-step-index="currentStepIndex + 1"
            :is-visible="showOnboarding"
            :action-url="onboardingActionUrl"
            :action-label="onboardingActionLabel"
            @close="hideOnboarding"
        />

        <div
            class="mobile-header-bar ppv2-w-[100vw] ppv2-bg-black ppv2-bg-opacity-[8%] ppv2-flex ppv2-justify-between ppv2-items-center ppv2-pl-3 ppv2-py-4"
        >
            <!-- Left: Hamburger + Title -->
            <div class="ppv2-flex ppv2-items-center ppv2-gap-3 w-75">
                <button class="mobile-hamburger-btn ppv2-text-black" @click="openSidebar">
                    <Menu :size="24" />
                </button>
                <div class="titleHeaderMobile poppinsRegular text-truncate ppv2-text-black">
                    {{ $t(currentIcon.title) }}
                </div>
            </div>

            <!-- Right: Content of NavBar.vue adapted -->
            <NavBar v-bind="$props" :currentRoute="localRoute" class="mobile-navbar-overrides" />
        </div>
    </div>
</template>

<script>
import { Menu } from 'lucide-vue'
import { EventBus } from '../../eventBus.js'
import OnboardingBanner from '../Shared/OnboardingBanner.vue'
import NavBar from './Navbar/NavBar.vue'
import normalizeSize from '../../utils/normalizeSize.js'

export default {
    name: 'HeaderMobile',
    components: {
        Menu,
        OnboardingBanner,
        NavBar,
    },
    props: {
        currentRoute: { type: String, default: '' },
        hasFeatureChangeAccount: { type: Boolean, default: false },
        hasFeatureParrinage: { type: Boolean, default: false },
        hasFeatureFormations: { type: Boolean, default: false },
        hasFeatureSubscription: { type: Boolean, default: true },
        isSubscribed: { type: Boolean, default: false },
        user: { type: Object, default: null },
        roles: { type: Array, default: () => [] },
        hasPublishedExperiences: { type: Boolean, default: false },
        establishmentSettingsStatus: {
            type: Object,
            default: () => ({
                has_name: false,
                has_language: false,
                has_company_name: false,
                has_timezone: false,
                has_registration_number: false,
                has_address: false,
                has_hours: false,
                is_complete: false,
            }),
        },
        establishmentId: { type: [String, Number], default: 1 },
    },
    data() {
        return {
            localRoute: this.currentRoute,
            sidebarOpen: false,
            showOnboarding: false, // Initially false
            currentStepIndex: 0,
            onboardingSteps: [],
            isAllOnboardingCompleted: true,
            routeIcons: {
                // Legacy routes
                adminHome: { title: 'sidebar.dashboard' },
                adminPlanning: { title: 'sidebar.calendar' },
                getCheckouts: { title: 'sidebar.nomad_checkout' },
                getCustomersBOV2: { title: 'sidebar.sales' },
                getCustomersBOV3: { title: 'sidebar.customers' },
                createCustomerV3: { title: 'sidebar.customers' },
                editCustomerV3: { title: 'sidebar.customers' },
                getDevis: { title: 'sidebar.quotes' },
                getInvoices: { title: 'sidebar.invoices' },
                getPromotions: { title: 'sidebar.annonces' },
                getPromotionsV3: { title: 'sidebar.promo_codes' },
                newsletters_index: { title: 'sidebar.newsletters' },
                getMailsCustoms: { title: 'sidebar.auto_mails' },
                notifications_index: { title: 'sidebar.push_notifications' },
                blog_list: { title: 'sidebar.blog' },
                staffPlanning: { title: 'sidebar.planning' },
                getStaffs: { title: 'sidebar.users' },
                getRoles: { title: 'sidebar.roles_access' },
                GamificationViewAll: { title: 'sidebar.rewards' },
                getStats: { title: 'sidebar.exports' },
                getStatisticsPage: { title: 'sidebar.dashboard' },
                editViewEstablishmentV2: { title: 'sidebar.establishment' },
                list_categ: { title: 'sidebar.website' },
                getSpaces: { title: 'sidebar.experiences' },
                getWebsiteSettings: { title: 'sidebar.resource' },
                widgets_index: { title: 'sidebar.widgets' },
                getProduit: { title: 'sidebar.products' },
                bo_foods_index: { title: 'sidebar.restoration' },
                google_calendar: { title: 'sidebar.quote_templates' },
                adminIndexPos: { title: 'sidebar.pos_devices' },
                adminIndexApplication: { title: 'sidebar.api' },
                getInvoicesV2: { title: 'sidebar.integration' },
                getInvoicesV3: { title: 'sidebar.invoices' },
                getInvoicesEnAttente: { title: 'sidebar.orders' },
                // V3 routes — experiences & equipment
                indexViewExperienceV3: { title: 'sidebar.experiences' },
                createViewExperienceV3: { title: 'sidebar.experiences' },
                createSimpleViewExperienceV3: { title: 'sidebar.experiences' },
                editViewExperienceV3: { title: 'sidebar.experiences' },
                indexViewEquipementsV3: { title: 'header.titles.indexViewEquipementsV3' },
                indexViewEspacesV3: { title: 'header.titles.indexViewEquipementsV3' },
                createViewEquipementsV3: { title: 'header.titles.indexViewEquipementsV3' },
                editViewEquipementsV3: { title: 'header.titles.indexViewEquipementsV3' },
                // V3 routes — food
                indexViewFoodV3: { title: 'header.titles.indexViewFoodV3' },
                createViewFoodV3: { title: 'header.titles.indexViewFoodV3' },
                editViewFoodV3: { title: 'header.titles.indexViewFoodV3' },
                // V3 routes — products
                indexViewProductV3: { title: 'sidebar.products' },
                indexViewProductCategoryV3: { title: 'sidebar.products' },
                indexViewProductSupplierV3: { title: 'sidebar.products' },
                indexViewProductBrandV3: { title: 'sidebar.products' },
                createProductV3: { title: 'sidebar.products' },
                editProductV3: { title: 'sidebar.products' },
                // V3 routes — annonces
                annonce_index: { title: 'sidebar.annonces' },
                // V3 routes — blogs
                v3_blogs_index: { title: 'sidebar.blog' },
                v3_add_blog: { title: 'sidebar.blog' },
                v3_edit_blog: { title: 'sidebar.blog' },
                // V3 routes — mails automatiques
                mailcustom_v3_edit: { title: 'sidebar.auto_mails' },
                // V3 routes — form questions / devis
                formQuestion_index: { title: 'sidebar.formQuestion' },
                // V3 routes — integrations
                integration_index: { title: 'sidebar.integration' },
                // V3 routes — exports
                getExportBOV3: { title: 'sidebar.accounting' },
                // V3 routes — POS
                getPosBOV3: { title: 'sidebar.pos_devices' },
                createPosV3: { title: 'sidebar.pos_devices' },
                editPosV3: { title: 'sidebar.pos_devices' },
                // V3 routes — staffs
                indextaffsV3: { title: 'sidebar.users' },
                createStaffV3: { title: 'sidebar.users' },
                editStaffV3: { title: 'sidebar.users' },
                // V3 routes — roles
                roles_v3_index: { title: 'sidebar.roles_access' },
                roles_v3_create: { title: 'sidebar.roles_access' },
                roles_v3_edit: { title: 'sidebar.roles_access' },
                // V3 routes — resources
                indexViewResourceV3: { title: 'sidebar.resource' },
                // V3 routes — packages & treatments
                createPackageV3: { title: 'sidebar.experiences' },
                resumePackageV3: { title: 'sidebar.experiences' },
                editPackageV3: { title: 'sidebar.experiences' },
                createTreatmentV3: { title: 'sidebar.sales' },
                resumeTreatmentV3: { title: 'sidebar.sales' },
                editTreatmentV3: { title: 'sidebar.sales' },
                getTreatmentsCustomersBOV3: { title: 'sidebar.sales' },
                // V3 routes — website settings
                websiteSettingsV3: { title: 'sidebar.website' },
                // V3  routes — quotes
                createDevisV3: { title: 'sidebar.quotes' },
                // V3 routes — planning
                bo_planning_v3_index: { title: 'sidebar.planning' },
                editPopUp: { title: 'sidebar.annonces' },
                createPopUp: { title: 'sidebar.annonces' },
                editPromotionV3: { title: 'sidebar.promo_codes' },
                createPromotionV3: { title: 'sidebar.promo_codes' },
                v2_model_temlate_devis_index: { title: 'sidebar.quote_templates' },
                adminIndexApplicationV3: { title: 'sidebar.api' },
                subscription_index: { title: 'navbar.subscription_billing' },
                helpdesk_index: { title: 'navbar.help_center' },
                getProfilBoV3: { title: 'navbar.manage_account' },
                //staff
                bo_planning_v3_staffs: { title: 'sidebar.planning' },
            },
        }
    },
    watch: {
        currentRoute(newVal) {
            this.localRoute = newVal
        },
    },
    computed: {
        currentIcon() {
            return this.routeIcons[this.localRoute.replace(/[.\-]/g, '_')] || { title: 'header.titles.default' }
        },
        currentOnboardingStep() {
            return this.onboardingSteps[this.currentStepIndex]
        },
        onboardingActionUrl() {
            return '/'
        },
        onboardingActionLabel() {
            return 'Config'
        },
    },
    mounted() {
        EventBus.$on('mobile-close-sidebar', this.onSidebarClosed)
        document.addEventListener('turbo:load', this.updateRouteFromMeta)
    },
    beforeDestroy() {
        EventBus.$off('mobile-close-sidebar', this.onSidebarClosed)
        document.removeEventListener('turbo:load', this.updateRouteFromMeta)
    },
    methods: {
        normalizeSize,
        updateRouteFromMeta() {
            const routeNameMeta = document.querySelector('meta[name="current-route-name"]')
            if (routeNameMeta) {
                this.localRoute = routeNameMeta.content
            }
        },
        onSidebarClosed() {
            this.sidebarOpen = false
        },
        openSidebar() {
            this.sidebarOpen = true
            EventBus.$emit('mobile-open-sidebar')
        },
        hideOnboarding() {
            this.showOnboarding = false
        },
    },
}
</script>

<style scoped>
.mobile-header-container {
    width: 100%;
    z-index: 50;
    position: sticky;
    top: 0;
}

.mobile-hamburger-btn {
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
}
.titleHeaderMobile {
    font-size: clamp(12px, 0.729545vw, 16px);
}
@media (max-width: 348px) {
    .titleHeaderMobile {
        font-size: 12px;
    }
}
</style>
